import { useEffect, useMemo, useState } from 'react';
import { useRxDatabase } from 'rxdb/plugins/react';
import type { HistoryFeedRow } from '@ast-data/shared';
import {
    historyOwnedRoute,
    historyRecordCollection,
    historyRecordPath,
    type HistoryRecordDoc,
    type HistoryRecordOwner
} from './historyRecordPath';

/** What one collection's live query last reported, keyed by document id. */
type TypeResult = Map<string, string>;

interface Subscription {
    unsubscribe: () => void;
}

interface LiveQuery {
    $: {
        subscribe: (
            next: (docs: { toJSON: () => unknown }[]) => void
        ) => Subscription;
    };
}

interface QueryableCollection {
    find: (query: unknown) => LiveQuery;
}

/**
 * The office route to each record a page of change-log rows is about, keyed by
 * `entityId`. A record with no entry has no link — it has no screen of its own,
 * it is gone, or it has not reached this device yet.
 *
 * Read from RxDB rather than from the log row for two reasons. The row cannot
 * address a Planum or Profil on its own (it carries no `findingId`), and a row
 * says what a record looked like at a moment, not whether it still exists.
 * RxDB's `find` skips deleted documents, so a record that is gone drops out and
 * the log offers no link to a screen that would wait for it for ever.
 *
 * The queries are LIVE, not one-shot. On a cold start the change log is on
 * screen long before replication has delivered the records it talks about, and
 * a lookup that ran once would leave the whole page linkless. Subscribing means
 * each link appears the moment its record arrives.
 *
 * One query per kind of record per page, each an `$in` over that page's ids —
 * so this stays bounded by the page size, not by how much the database holds.
 *
 * A Medium has no screen of its own; its link opens the description it belongs
 * to, scrolled to its photos (see historyOwnedRoute). That takes two steps —
 * the Medium names its owner, the owner knows its route — and the second query
 * is rebuilt whenever the first reports, so a Medium moved or orphaned since
 * follows along like every other record.
 */
export function useHistoryRecordPaths(
    rows: readonly Pick<HistoryFeedRow, 'entityType' | 'entityId'>[]
): Map<string, string> {
    const database = useRxDatabase();
    const [paths, setPaths] = useState<Map<string, string>>(new Map());

    // Grouped by entity type, not by collection: the type is what picks the
    // route, and the two Zeiterfassungen share a collection. Keyed by a plain
    // string so a repeated fetch of the same page does not restart the queries.
    const wantedKey = useMemo(() => {
        const byType = new Map<string, Set<string>>();
        for (const row of rows) {
            if (
                !historyRecordCollection(row.entityType) &&
                !historyOwnedRoute(row.entityType)
            ) {
                continue;
            }
            const ids = byType.get(row.entityType) ?? new Set<string>();
            ids.add(row.entityId);
            byType.set(row.entityType, ids);
        }
        return [...byType]
            .map(
                ([entityType, ids]) =>
                    `${entityType}:${[...ids].sort().join(',')}`
            )
            .sort()
            .join('|');
    }, [rows]);

    useEffect(() => {
        if (!database || !wantedKey) {
            return;
        }
        const collections = (
            database as unknown as {
                collections: Record<string, QueryableCollection | undefined>;
            }
        ).collections;

        // Each subscription owns its slice; the published map is their union,
        // rebuilt whenever any of them reports.
        const byType = new Map<string, TypeResult>();
        const publish = () => {
            const merged = new Map<string, string>();
            for (const found of byType.values()) {
                for (const [id, path] of found) {
                    merged.set(id, path);
                }
            }
            setPaths(merged);
        };

        /** A live `$in` query over `ids`, or null when the collection is absent. */
        const watch = (
            name: string | null,
            ids: string[],
            next: (records: HistoryRecordDoc[]) => void
        ): Subscription | null => {
            const collection = name ? collections[name] : undefined;
            if (!collection) {
                return null;
            }
            return collection
                .find({ selector: { id: { $in: ids } } })
                .$.subscribe((docs) =>
                    next(docs.map((doc) => doc.toJSON() as HistoryRecordDoc))
                );
        };

        // Torn down with the effect: the owner queries of the owned types,
        // replaced each time their first step reports.
        const ownerSubscriptions = new Map<string, Subscription[]>();

        const subscriptions = wantedKey.split('|').map((part) => {
            const [entityType, joined] = part.split(':');
            const ids = joined.split(',');
            const owned = historyOwnedRoute(entityType);
            if (!owned) {
                return watch(
                    historyRecordCollection(entityType),
                    ids,
                    (records) => {
                        const found: TypeResult = new Map();
                        for (const record of records) {
                            const path = historyRecordPath(entityType, record);
                            if (path) {
                                found.set(record.id, path);
                            }
                        }
                        byType.set(entityType, found);
                        publish();
                    }
                );
            }
            return watch(owned.collection, ids, (records) => {
                // The owned records, grouped under each owner they name.
                const byOwnerType = new Map<string, Map<string, string[]>>();
                for (const record of records) {
                    const owner: HistoryRecordOwner | null =
                        owned.owner(record);
                    if (!owner) {
                        continue;
                    }
                    const owners =
                        byOwnerType.get(owner.entityType) ??
                        new Map<string, string[]>();
                    owners.set(owner.id, [
                        ...(owners.get(owner.id) ?? []),
                        record.id
                    ]);
                    byOwnerType.set(owner.entityType, owners);
                }

                ownerSubscriptions
                    .get(entityType)
                    ?.forEach((subscription) => subscription.unsubscribe());
                // An owner type no longer named takes its links with it; the
                // others keep theirs until their new query reports.
                for (const key of [...byType.keys()]) {
                    if (
                        key.startsWith(`${entityType}>`) &&
                        !byOwnerType.has(key.slice(entityType.length + 1))
                    ) {
                        byType.delete(key);
                    }
                }
                publish();

                const next = [...byOwnerType].map(([ownerType, owners]) =>
                    watch(
                        historyRecordCollection(ownerType),
                        [...owners.keys()],
                        (ownerRecords) => {
                            const found: TypeResult = new Map();
                            for (const ownerRecord of ownerRecords) {
                                const path = historyRecordPath(
                                    ownerType,
                                    ownerRecord
                                );
                                if (!path) {
                                    continue;
                                }
                                for (const id of owners.get(ownerRecord.id) ??
                                    []) {
                                    found.set(id, `${path}#${owned.anchor}`);
                                }
                            }
                            byType.set(`${entityType}>${ownerType}`, found);
                            publish();
                        }
                    )
                );
                ownerSubscriptions.set(
                    entityType,
                    next.filter(
                        (subscription): subscription is Subscription =>
                            subscription !== null
                    )
                );
            });
        });

        return () => {
            for (const subscription of subscriptions) {
                subscription?.unsubscribe();
            }
            for (const owners of ownerSubscriptions.values()) {
                owners.forEach((subscription) => subscription.unsubscribe());
            }
        };
    }, [database, wantedKey]);

    return paths;
}
